import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, fmtDateTime } from "@/lib/format";
import { emit } from "@/lib/events";
import { Button } from "@/components/ui/button";

export default function OrderSuccess() {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (user === undefined) return; // auth still loading
    const url = user ? `/orders/${id}` : `/orders/public/${id}`;
    api.get(url).then((r) => setOrder(r.data)).catch(() => {});
  }, [id, user]);

  useEffect(() => {
    if (!order) return;
    const key = `ev_purchase_${id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    emit("purchase", {
      value: order.total,
      transaction_id: order.order_number,
      payment_type: order.payment_method,
      items: (order.items || []).map((i) => ({
        item_id: i.sku || i.variant_id || i.product_id || "",
        item_name: i.title || "",
        price: i.price,
        quantity: i.qty,
      })),
    }, { email: order.customer?.email || user?.email, phone: order.customer?.phone || user?.phone });
  }, [order, id, user]);

  return (
    <div className="container-x py-16 max-w-2xl mx-auto text-center" data-testid="order-success-page">
      <CheckCircle2 size={56} className="mx-auto text-emerald-600" />
      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-4">Order confirmed</h1>
      <p className="text-neutral-600 mt-2">
        Thank you! Your order has been placed successfully.
      </p>
      {order && (
        <div className="border border-neutral-200 bg-neutral-50 mt-8 p-6 text-left" data-testid="order-success-details">
          <div className="flex justify-between text-sm">
            <span className="text-neutral-500">Order number</span>
            <span className="font-mono font-semibold" data-testid="order-success-number">{order.order_number}</span>
          </div>
          <div className="flex justify-between text-sm mt-2">
            <span className="text-neutral-500">Total</span>
            <span className="font-heading font-bold">{inr(order.total)}</span>
          </div>
          <div className="flex justify-between text-sm mt-2">
            <span className="text-neutral-500">Payment</span>
            <span className="font-mono uppercase text-xs">{order.payment_method} · {order.payment_status}</span>
          </div>
          <div className="flex justify-between text-sm mt-2">
            <span className="text-neutral-500">Placed</span>
            <span>{fmtDateTime(order.created_at)}</span>
          </div>
        </div>
      )}
      <div className="flex justify-center gap-3 mt-8">
        {user ? (
          <Link to={`/account/orders/${id}`}>
            <Button className="bg-neutral-900 hover:bg-brand text-white btn-press" data-testid="track-order-btn">Track Order</Button>
          </Link>
        ) : (
          <Link to="/login">
            <Button className="bg-neutral-900 hover:bg-brand text-white btn-press" data-testid="login-to-track-btn">Log in to Track</Button>
          </Link>
        )}
        <Link to="/shop">
          <Button variant="outline" className="btn-press" data-testid="continue-shopping-btn">Continue Shopping</Button>
        </Link>
      </div>
    </div>
  );
}
